import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { capitalize } from '@/lib/utils'

export default function Dashboard({ currentUser, conversations = [], newTriageUrl, agents = [] }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <h1 className="text-xl font-bold">Live Triage</h1>
          <div className="flex items-center gap-4">
            {newTriageUrl && (
              <Link href={newTriageUrl}>
                <Button size="sm">New triage</Button>
              </Link>
            )}
            {currentUser?.role === 'admin' && (
              <>
                <Link href="/admin/agents">
                  <Button variant="ghost" size="sm">Agents</Button>
                </Link>
                <Link href="/admin/organization">
                  <Button variant="ghost" size="sm">Settings</Button>
                </Link>
              </>
            )}
            <span className="text-sm text-muted-foreground">{currentUser?.email}</span>
            <Link href="/users/sign_out" method="delete" as="button">
              <Button variant="outline" size="sm">Sign out</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10">
        {agents.length > 1 && (
          <div className="mb-8 rounded-md border bg-muted/30 px-4 py-3 text-sm">
            <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Public triage URLs</div>
            <ul className="space-y-1">
              {agents.map((a) => (
                <li key={a.slug}>
                  <span className="font-medium">{a.name}:</span>{' '}
                  <a href={a.url} target="_blank" rel="noreferrer" className="font-mono text-xs underline-offset-4 hover:underline">
                    {a.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-semibold tracking-tight">Conversations</h2>
          <p className="text-sm text-muted-foreground">
            {conversations.length === 0
              ? 'No conversations yet — share an agent URL or click "New triage" to start one.'
              : `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}.`}
          </p>
        </div>

        {conversations.length > 0 && (
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">First name</th>
                  <th className="px-4 py-3">Last name</th>
                  <th className="px-4 py-3">DOB</th>
                  <th className="px-4 py-3">Reason for visit</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">ESI</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {conversations.map((c) => (
                  <ConversationRow key={c.id} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}

function ConversationRow({ c }) {
  const urgent = c.urgent
  const rowClass = `cursor-pointer transition-colors ${
    urgent ? 'bg-red-50 text-red-900 hover:bg-red-100' : 'hover:bg-muted/40'
  }`
  const target = c.patient_id ? `/patients/${c.patient_id}` : `/admin/conversations/${c.id}`

  return (
    <tr className={rowClass} onClick={() => router.visit(target)}>
      <td className="px-4 py-3 text-xs text-muted-foreground">{c.agent_name}</td>
      <td className="px-4 py-3 font-medium">{capitalize(c.first_name) || '—'}</td>
      <td className="px-4 py-3 font-medium">{capitalize(c.last_name) || '—'}</td>
      <td className="px-4 py-3">{formatDate(c.date_of_birth)}</td>
      <td className="px-4 py-3 max-w-md truncate" title={c.chief_complaint || ''}>
        {capitalize(c.chief_complaint) || '—'}
      </td>
      <td className="px-4 py-3 text-xs">{c.status}</td>
      <td className="px-4 py-3">
        <EsiBadge score={c.estimated_esi_score} urgent={urgent} />
      </td>
    </tr>
  )
}

function EsiBadge({ score, urgent }) {
  if (score == null) {
    return <span className="text-muted-foreground">—</span>
  }
  const className = urgent
    ? 'inline-flex h-6 items-center rounded-md border border-red-600/30 bg-red-600 px-2 text-xs font-semibold text-white'
    : 'inline-flex h-6 items-center rounded-md border bg-muted px-2 text-xs font-medium'
  return <span className={className}>ESI {score}</span>
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
